import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, Shield, HelpCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Dropdown, DropdownItem, DropdownSeparator } from './ui/Dropdown';
import UserAvatar from './profile/UserAvatar';
import { formatRating } from '../utils/format';

const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleAccountSettings = () => {
    navigate('/settings');
  };

  const handleSupport = () => {
    navigate('/support');
  };

  const getRatingColor = (rating: number) => {
    if (rating < 25) return 'text-green-600 dark:text-green-400';
    if (rating < 35) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getRatingBadge = (rating: number) => {
    if (rating < 20) return 'Beginner';
    if (rating < 30) return 'Intermediate';
    if (rating < 40) return 'Advanced';
    return 'Expert';
  };

  const displayName = user?.username || user?.email || 'User';

  const trigger = (
    <div className="flex items-center space-x-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg px-3 py-2 transition-colors cursor-pointer group">
      {/* User Avatar */}
      <UserAvatar
        iconUrl={user?.selected_icon?.icon_url}
        username={displayName}
        size="md"
      />

      {/* User Info */}
      <div className="flex items-center space-x-2 min-w-0">
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {displayName}
          </span>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-600 dark:text-gray-400">Rating:</span>
            <span className={`text-xs font-bold ${getRatingColor(user?.rating || 30)}`}>
              {formatRating(user?.rating ?? 30)}
            </span>
            <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-mango-100 text-mango-800 dark:bg-mango-900 dark:text-mango-200">
              {getRatingBadge(user?.rating || 30)}
            </span>
          </div>
        </div>

        {/* Dropdown Arrow */}
        <svg
          className="h-4 w-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );

  return (
    <Dropdown trigger={trigger} align="right">
      {/* Account Settings */}
      <DropdownItem onClick={handleAccountSettings}>
        <div className="flex items-center space-x-2">
          <Settings className="h-4 w-4" />
          <span>Account Settings</span>
        </div>
      </DropdownItem>

      {/* Support */}
      <DropdownItem onClick={handleSupport}>
        <div className="flex items-center space-x-2">
          <HelpCircle className="h-4 w-4" />
          <span>Support</span>
        </div>
      </DropdownItem>

      {/* Admin Panel - Only show for admin users */}
      {user?.is_admin && (
        <>
          <DropdownSeparator />
          <DropdownItem onClick={() => navigate('/admin')}>
            <div className="flex items-center space-x-2">
              <Shield className="h-4 w-4" />
              <span>Admin Panel</span>
            </div>
          </DropdownItem>
        </>
      )}

      <DropdownSeparator />

      {/* Sign Out */}
      <DropdownItem onClick={handleLogout} danger={true}>
        <div className="flex items-center space-x-2">
          <LogOut className="h-4 w-4" />
          <span>Sign Out</span>
        </div>
      </DropdownItem>
    </Dropdown>
  );
};

export default UserMenu;
